/**
 * CapabilityProvider.tsx — the capability boundary's React context root
 * (TG-4).
 *
 * Fails closed IN THE RENDER PATH (Secure by Design, STRIDE THR-5/THR-11):
 * on any of `decodeClaim`'s four denial cases (absent, schema-invalid,
 * role-missing, unrecognised-role — ADR-20260826-82qe), `children` are
 * never mounted at all. `AccessNotConfirmed` renders instead, so a
 * consumer calling `useCapability()` (`./use-capability`) can never
 * observe a "denied" `Capability` value and mistake it for a legitimate
 * contributor holding zero delegated sections — the two are otherwise
 * structurally identical (`Capability` carries no field a caller could use
 * to tell them apart; see `capability.ts`'s own `DENIED_CAPABILITY`). The
 * guard's failure mode is absence, never exposure.
 *
 * `decodeClaim` and `resolveCapability` are both called here, inside
 * `capability/` — permitted under ADR-20260826-79rv/ADR-20260826-21nr's
 * containment rule, which only forbids a module OUTSIDE this directory
 * from reaching `./claim`. Neither call reads `principalRole` itself: this
 * file only branches on `decodeClaim`'s null-ness, never on the decoded
 * value's fields, so it adds no second reader of the role to the one
 * `resolveCapability` already is (AC-1, `__tests__/containment.test.ts`).
 *
 * `claim`'s type is inferred the same way `capability.ts`'s own
 * `resolveCapability` parameter is (`Parameters<typeof decodeClaim>[0]`),
 * never a named import of `claim.ts`'s unexported `RawSessionClaim` --
 * there is nothing to import by name in the first place.
 *
 * `RenewalIdContext` (OBJ-S-3/OBJ-C-5, FIND-283/FIND-288, ruled
 * 2026-10-08): `getRenewalId` (`./renewal-id`) takes the raw claim as its
 * only argument, so no feature module outside `capability/` could ever
 * reach a `renewalId` through it at all -- containment forbids feature
 * modules from holding the raw claim in the first place. Mounted here,
 * alongside `CapabilityContext`, from the SAME decode this component
 * already performs (no second `decodeClaim` reader of anything beyond
 * what this file already reads). `./renewal-id`'s `useRenewalId()` hook
 * is the sanctioned, context-backed way a feature module reaches it now.
 *
 * Cross-root single-mount latch (OBJ-S-10, FIND-362/FIND-369, debate turn
 * 5): the `CapabilityContext` read below only detects a nested
 * second mount IN THE SAME React tree -- a feature module that starts a
 * SEPARATE root (`createRoot(el).render(<CapabilityProvider .../>)`, e.g.
 * from a `useEffect`) begins a fresh context chain in which `useContext`
 * returns the default `null`, so that guard alone never fires. `react-dom`
 * carries no import edge any containment rule can see either. `activeMount`
 * is module-scope state, shared by every root that loads this module, so it
 * is checked here regardless of which React tree is rendering.
 *
 * Acquired (checked AND written) entirely inside a layout effect, never
 * during the render body (OBJ-R-22, FIND-per-brief, fixed 2026-10-09).
 *
 * Previously the conflict check ran during render, with only the
 * acquire deferred to the layout effect. That false-positived on a
 * legitimate remount of the SAME logical provider -- under
 * `<StrictMode>`'s dev-only double-invoke, or whenever this element's
 * `key` changes -- because React always finishes rendering the incoming
 * instance before it starts the commit-phase work that runs the
 * outgoing instance's layout-effect cleanup. At render time the old
 * instance's acquisition is therefore still held, and the new instance's
 * render-phase check could not tell a legitimate same-provider remount
 * apart from a genuine second, conflicting mount: both look identical
 * from inside render.
 *
 * That ordering IS resolved by the time any layout effect runs: React
 * guarantees that within one commit, an outgoing tree's layout-effect
 * cleanups fire (in the mutation phase) before an incoming tree's
 * layout-effect setups fire (in the following layout phase). Moving the
 * check-and-acquire into the layout effect means a legitimate same-
 * commit remount always sees the latch already released by the time it
 * checks, while a genuinely separate, overlapping mount (a different
 * root, never unmounting) still sees the latch held and still throws --
 * the detached-root cases in `nested-provider-guard.test.tsx` continue
 * to exercise exactly that.
 *
 * This does defer the throw past the point children are committed to
 * this root's own DOM (layout effects run post-commit, pre-paint) --
 * but not past the point the browser paints: layout effects are
 * synchronous and block paint, and React unwinds (unmounts) the whole
 * offending root synchronously when an effect throws with no error
 * boundary above it, before yielding back to the event loop. No frame is
 * ever produced with the fabricated content on screen, and the other,
 * legitimate root is untouched (errors do not cross React root
 * boundaries). A render-phase check could reject before any DOM commit
 * at all, but render-phase is structurally unable to distinguish the two
 * cases here (see above) -- not an option we can keep and still fix the
 * false positive.
 *
 * THE NO-FRAME-PAINTED CLAIM ABOVE IS ABOUT PAINT, NOT EXECUTION (OBJ-S-14,
 * FIND-409, debate iteration 1 post-8d0001a; documented limit, not a
 * further guard -- see LIMITS.md case 4's "RESIDUAL-F"). React runs a
 * commit's layout effects bottom-up: every descendant's own render AND
 * its own layout effect run before THIS component's layout effect (the
 * one that throws) ever does. A child under a fabricated, detached-root
 * `CapabilityProvider` therefore renders and runs its own layout effects
 * -- reading `useCapability()` as the fabricated grant, genuinely true
 * for that window, not a stale value -- before this latch gets a chance
 * to throw. Measured directly: a child's `useLayoutEffect` observed
 * `canSubmitRenewal === true` and `document.body.textContent` contained
 * "Submit renewal" at that moment, then the latch threw and the root
 * unwound. Nothing on screen is ever painted, exactly as this comment
 * already said -- but any child code that runs a real side effect from
 * its own render, layout effect OR PASSIVE EFFECT (`useEffect`) runs for
 * real in that window regardless (a network call, a write to storage,
 * anything not gated on paint). THE WINDOW IS NOT LAYOUT-EFFECT-ONLY
 * (OBJ-S-14, FIND-412, re-raised debate iteration 2, fixed 2026-10-09):
 * measured directly, a child's passive `useEffect` also observed
 * `canSubmitRenewal === true` with "Submit renewal" still in
 * `document.body.textContent` -- React defers passive effects to a
 * microtask/macrotask after layout effects and after the latch has
 * already thrown for THIS component, but a fabricated root's own
 * descendant's passive effect still ran with the fabricated grant
 * active before its own tree unwound. This feature's own route-level
 * consumers that run side effects from `useEffect` rather than a layout
 * effect -- `ConfirmDetailsRoute.tsx`, `MultiSectionStepRoute.tsx`,
 * `RenewalLanding.tsx`, `ChangeCheckRoute.tsx` (OBJ-C-42, FIND-421, fixed
 * 2026-10-09 -- its own two passive effects were omitted from this
 * enumeration) -- are each within this blast radius; none of
 * this feature's own wrappers (`SectionConfirmControl`,
 * `DeclarationRouteWrapper`) does a real side effect from render, a
 * layout effect OR a passive effect today, but nothing in `capability/`
 * enforces that no future consumer, in any of those three hooks, ever
 * will.
 */
import { createContext, useContext, useLayoutEffect, useState } from "react";
import type { ReactNode } from "react";

import { resolveCapability } from "./capability";
import type { Capability } from "./capability";
import { decodeClaim } from "./claim";
import { AccessNotConfirmed } from "./components/AccessNotConfirmed";

/**
 * `null` means "no `CapabilityProvider` is mounted above this point" --
 * `useCapability` (below, re-exported by `./use-capability`) treats that
 * as a programmer error and throws, exactly as the retired stub did. It
 * is never the value a mounted `CapabilityProvider` supplies for a
 * denied claim: that case renders `AccessNotConfirmed` instead of a
 * context value at all.
 *
 * NOT exported, under any name, from this file or any other (OBJ-S-12,
 * fixed 2026-10-09). Before this fix, `CapabilityContext` was a named
 * export, reachable by anyone willing to write a new file anywhere under
 * `capability/` that re-exported it under a fresh name dependency-
 * cruiser's path-based rules did not yet know about -- `export {
 * CapabilityContext } from "./CapabilityProvider"` from a never-before-
 * seen path, imported from outside `capability/`, let a feature module
 * mount `<CapabilityContext.Provider value={fabricated}>` directly,
 * skipping `decodeClaim`/`resolveCapability` and this file's mount latch
 * entirely. Each prior fix (`OBJ-S-6`, `OBJ-C-17`, the `-external-reader`
 * fixture) closed one demonstrated bridge path; a new bridge file always
 * reopened the same class. A binding that is never exported cannot be
 * re-exported by anything, so this closes the whole class for
 * `CapabilityContext` ITSELF structurally.
 *
 * CORRECTED (OBJ-C-34/OBJ-R-23, FIND-405/FIND-410, debate iteration 1
 * post-8d0001a): the sentence above does NOT extend to the bridge class in
 * general, and a prior version of this comment claimed it did ("closes the
 * whole class structurally", unqualified) -- that was false. `useCapability`
 * and `ProvideCapabilityForTest`, the two sanctioned ways to reach a
 * resolved `Capability`, ARE exported from this file (necessarily -- they
 * are the public surface) and re-exported (not re-implemented) by
 * `./use-capability` and `./test-support` so their own existing public
 * import paths do not change for any consumer. A bridge file placed
 * anywhere under `capability/` re-exporting either of THOSE under a fresh
 * name is exempt from this file's own `to`-side containment rule exactly
 * as the retired `CapabilityContext` bridge was, and was reproduced doing
 * so with every other gate green. What actually closes that general class
 * is `.dependency-cruiser.js`'s `no-reach-into-non-public-capability-files`
 * rule: an ENUMERATED allowlist of the only files under `capability/` any
 * outside module may import at all, so a bridge re-exporting anything --
 * `ProvideCapabilityForTest`, a future addition, anything -- is caught
 * because it is not on the list, not because the specific value it
 * re-exports is named anywhere.
 *
 * BOTH "closes structurally" CLAIMS ABOVE (line ~153 and the allowlist
 * claim just above) ARE FALSE AGAINST A ROUTE THAT CREATES NO IMPORT EDGE
 * AT ALL (OBJ-S-40, FIND-667, debate iteration 1). A module recovering
 * this context object directly from React's own fiber tree at runtime --
 * `Object.keys(domNode).find(k => k.startsWith("__reactFiber$"))`, walk
 * `.return` to the provider fiber, confirm via `fiber.type.$$typeof ===
 * Symbol.for("react.context")`, then `createElement(fiber.type, {value:
 * fabricated}, children)` for its own subtree -- never imports anything
 * from `capability/` beyond the sanctioned `useCapability` a legitimate
 * consumer already needs, so neither the never-exported binding above nor
 * the dependency-cruiser allowlist below has anything to see: no
 * `CapabilityContext` import, no bridge file, no re-export, no
 * `principalRole` text, no role branch. REPRODUCED 2026-10-13: a
 * production-shaped sibling subtree rendered under the real
 * `CapabilityProvider` escalated a contributor claim to owner-gated
 * controls this way, with every other gate (eslint, dependency-cruiser,
 * tsc, `containment.test.ts`) green. Narrowed, not closed, by a textual
 * scan for these literal markers outside `capability/`
 * (`__tests__/react-internals-containment-scan.test.ts`) -- the same
 * textual-scan caveat `DEBT-051`'s own scan carries applies here too.
 *
 * A SECOND, DISTINCT ROUTE SHARES NO MARKER WITH THE ABOVE (OBJ-S-41,
 * FIND-687, debate turn 3): a module importing only `react` patches
 * React's shared-internals dispatcher surface
 * (`React.__CLIENT_INTERNALS_DO_NOT_USE_OR_WARN_USERS_THEY_CANNOT_UPGRADE.H.useContext`,
 * or react-dom's parallel `__DOM_INTERNALS_...` export) so that EVERY
 * `useCapability()` call tree-wide -- not one subtree -- returns a
 * fabricated owner `Capability`. It walks no fiber and re-provides no
 * context object, so it is a different mechanism from the fiber walk
 * above, not a variant of it. The scan above was widened to name these
 * exports too, with the same narrowing (not closing) caveat.
 *
 * THE DIVIDING LINE IS NOT OBFUSCATION (OBJ-S-42, FIND-695, debate turn
 * 4): a marker-free variant of the fiber walk above --
 * `Object.values(domNode).find(v => "return" in v)`, walking `.return`
 * until this `Capability`'s own public field name (e.g.
 * `canSubmitRenewal`) appears in `memoizedProps.value` -- reaches and
 * re-provides this same context while spelling NONE of the scan's
 * literal markers and using nothing but ordinary, non-obfuscated surface
 * (`Object.values`, the `in` operator, `memoizedProps`, `createElement`).
 * REPRODUCED 2026-10-13: 1/1, scan passing with zero offenders. A plain,
 * non-obfuscated instance of this gap already has no automated control --
 * the scan only ever catches a walk that spells one of its specific
 * markers, not fiber-walk shape in general.
 *
 * The structural fix (resolving `Capability` through a module-private
 * `WeakMap` keyed by a per-mount opaque token, validating the resolved
 * value against the module-private registry rather than trusting
 * `useContext`'s or the dispatcher's return, so neither of the two routes
 * above has an entry and `useCapability` throws) is a larger rewrite than
 * a single debate-turn fix and is tracked as `DEBT-052`, covering both
 * routes, for a human decision on whether to take it. Until then,
 * human review of capability rendering is the standing control -- the
 * textual scan is a secondary signal, not a backstop.
 */
const CapabilityContext = createContext<Capability | null>(null);

/** `null` means "no `CapabilityProvider` is mounted above this point",
 * exactly as `CapabilityContext`'s own `null` does -- see `./renewal-id`'s
 * `useRenewalId` for the consumer side. */
export const RenewalIdContext = createContext<string | null>(null);

/**
 * Module-scope mount latch (OBJ-S-10, FIND-369) -- not component state,
 * deliberately: it must be visible to every `CapabilityProvider` instance
 * loaded from this module, including one mounted in a second, unrelated
 * React root created via `createRoot`, which a per-tree `useContext` read
 * can never see. `null` means no `CapabilityProvider` currently holds the
 * latch anywhere in the process.
 *
 * Both the conflict CHECK and the acquire happen together, inside the
 * layout effect below (OBJ-R-22, fixed 2026-10-09) -- see this file's own
 * top-of-file comment for why a render-phase check could not stay and
 * still fix the false positive on a legitimate remount.
 */
let activeCapabilityProviderMount: symbol | null = null;

export interface CapabilityProviderProps {
  readonly claim: Parameters<typeof decodeClaim>[0];
  readonly children: ReactNode;
}

export function CapabilityProvider({ claim, children }: Readonly<CapabilityProviderProps>) {
  // OBJ-S-10 (FIND-362, debate turn 4): App.tsx mounts the single,
  // legitimate `CapabilityProvider` at the composition root. A second,
  // nested mount anywhere below it is therefore never a legitimate
  // second grant -- by construction it is a feature module fabricating
  // its own claim (from any source: hand-built, JSON.parse'd, decoded
  // from an attacker-controlled string) and supplying it directly to
  // this component, bypassing every import-edge-based containment rule
  // because it imports nothing forbidden to do so. This throw is a
  // control, not a disclosure: it fails closed on the one shape no
  // dependency-cruiser rule can see, since no import edge is created.
  // Reading the context here adds no second reader of the decoded
  // claim's fields -- this only checks presence/absence of a context
  // value, never branches on `principalRole` or any other field of it
  // (AC-1 containment is unaffected).
  const existingCapability = useContext(CapabilityContext);
  if (existingCapability !== null) {
    throw new Error(
      "CapabilityProvider must be mounted exactly once, at the composition root. A nested " +
        "CapabilityProvider was found inside an already-provided capability boundary -- this is " +
        "never a legitimate second grant, so it is refused rather than silently honoured.",
    );
  }

  // OBJ-S-10 (FIND-369, debate turn 5): the context read above cannot see a
  // CapabilityProvider mounted in a SEPARATE React root. `mountToken` is a
  // stable per-instance identity (the `useState` initializer runs once per
  // mounted instance, surviving re-renders of that same instance) so a
  // component re-rendering itself is never mistaken for a second,
  // conflicting mount.
  const [mountToken] = useState<symbol>(() => Symbol("CapabilityProvider mount"));

  // OBJ-R-22 (fixed 2026-10-09): check-and-acquire both happen here, inside
  // the layout effect, never in the render body. React guarantees that
  // within one commit an outgoing tree's layout-effect cleanups run before
  // an incoming tree's layout-effect setups do, so a legitimate remount of
  // the SAME logical provider (StrictMode's dev-only double-invoke, or a
  // `key` change forcing unmount+remount) always finds the latch already
  // released by the time this runs. A genuinely separate, overlapping
  // mount -- a different root that never unmounts -- still finds the
  // latch held and still throws here, before paint (see top-of-file
  // comment for the full reasoning and its one accepted trade-off).
  useLayoutEffect(() => {
    if (activeCapabilityProviderMount !== null && activeCapabilityProviderMount !== mountToken) {
      throw new Error(
        "CapabilityProvider must be mounted exactly once, at the composition root. A second " +
          "CapabilityProvider mount was detected in a separate React root -- this is never a " +
          "legitimate second grant, so it is refused rather than silently honoured.",
      );
    }
    activeCapabilityProviderMount = mountToken;
    return () => {
      if (activeCapabilityProviderMount === mountToken) {
        activeCapabilityProviderMount = null;
      }
    };
  }, [mountToken]);

  const decoded = decodeClaim(claim);
  if (decoded === null) {
    return <AccessNotConfirmed />;
  }

  return (
    <CapabilityContext.Provider value={resolveCapability(claim)}>
      <RenewalIdContext.Provider value={decoded.renewalId}>{children}</RenewalIdContext.Provider>
    </CapabilityContext.Provider>
  );
}

/**
 * Reads the resolved {@link Capability} a consuming component renders
 * against. Throws outside a {@link CapabilityProvider} rather than
 * silently defaulting to "everything permitted" or "nothing permitted" --
 * either silent default would be exactly the kind of capability mistake
 * ADR-20260826-ejck exists to make impossible to reach unnoticed. Never
 * returns a "denied" value for a failed claim: `CapabilityProvider` itself
 * renders `AccessNotConfirmed` instead of mounting children in that case,
 * so this function is only ever reached once access is confirmed.
 *
 * Implemented here, not in `./use-capability` (OBJ-S-12, fixed
 * 2026-10-09), because it is the only place `CapabilityContext` -- now a
 * module-private binding -- is in scope. `./use-capability` re-exports
 * this function unchanged; every existing import of `useCapability` from
 * that path keeps working exactly as before.
 */
export function useCapability(): Capability {
  const capability = useContext(CapabilityContext);
  if (capability === null) {
    throw new Error("useCapability() called outside a CapabilityProvider — no permitted-action set is available.");
  }
  return capability;
}

export interface ProvideCapabilityForTestProps {
  readonly capability: Capability;
  readonly children: ReactNode;
}

/**
 * Test-only. Mounts `children` under a `CapabilityContext.Provider`
 * carrying exactly the supplied `Capability` -- no claim decoding, no
 * denial branch, because a test exercising capability-gated rendering
 * needs to supply arbitrary (including edge-case) `Capability` shapes
 * directly, not round-trip them through a fabricated raw claim. This is
 * the ONE sanctioned seam for a test outside `capability/` to render
 * against a hand-built `Capability` (OBJ-S-2/FIND-282, ruled 2026-10-08).
 *
 * Implemented here, not in `./test-support` (OBJ-S-12, fixed 2026-10-09),
 * for the same reason as `useCapability` above: `CapabilityContext` is a
 * module-private binding, only in scope in this file. `./test-support`
 * re-exports this function unchanged; its own existing public import path
 * keeps working exactly as before.
 */
export function ProvideCapabilityForTest({ capability, children }: Readonly<ProvideCapabilityForTestProps>) {
  return <CapabilityContext.Provider value={capability}>{children}</CapabilityContext.Provider>;
}
